// eslint-disable-next-line max-classes-per-file
import type {IHttpPromise, IHttpService, IRequestShortcutConfig} from 'angular';
import {responseToData, serializeUrlParams} from 'shared/utils/data-service-utils';
import {exists} from 'shared/utils/custom-utils';
import {assertIsNonEmptyString} from 'shared/utils/string-utils';


export class AjaxServiceProvider {
    public static providerName = 'ajaxService';

    public readonly $get: (_: IHttpService) => AjaxService;

    private baseUrl: string;

    constructor() {
        this.$get = /*@ngInject*/ ($http: IHttpService) => this.createAjaxService($http);
    }

    public setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private createAjaxService($http: IHttpService): AjaxService {
        return new AjaxService($http, this.baseUrl);
    }
}

export class AjaxService {

    constructor(private $http: IHttpService, private baseUrl: string) {
    }

    createRequest(url: string): AjaxRequest {
        assertIsNonEmptyString(url);
        const requestUrl = url.at(0) === '/' ? `${this.baseUrl}${url}` : `${this.baseUrl}/${url}`;
        return new AjaxRequest(this.$http, requestUrl);
    }

    withoutBaseUrl(): AjaxService {
        return new AjaxService(this.$http, '')
    }
}

class AjaxRequest {
    private headers: Record<string, any>;
    private responseType: XMLHttpRequestResponseType;
    private params: Record<string, any>;
    private body: RequestBody;

    constructor(private $http: IHttpService, private baseUrl: string) {
    }

    public withBody(body: RequestBody): AjaxRequest {
        this.body = body;
        return this;
    }

    public withHeaders(headers: Record<string, any>): AjaxRequest {
        this.headers = headers;
        return this;
    }

    public withResponseType(responseType: XMLHttpRequestResponseType): AjaxRequest {
        this.responseType = responseType;
        return this;
    }

    public async get<RESPONSETYPE>(params?: Record<string, any>): Promise<RESPONSETYPE> {
        this.params = params;

        return this.extractResponse(this.sendRequest('get'));
    }

    public async post<RESPONSETYPE>(body: RequestBody | null, params?: Record<string, any>): Promise<RESPONSETYPE> {
        this.params = params;
        this.body = body;

        return this.extractResponse(this.sendRequest('post'));
    }

    public async put<RESPONSETYPE>(body: RequestBody | null, params?: Record<string, any>): Promise<RESPONSETYPE> {
        this.params = params;
        this.body = body;

        return this.extractResponse(this.sendRequest('put'));
    }

    public async delete<RESPONSETYPE>(params?: Record<string, any>): Promise<RESPONSETYPE> {
        this.params = params;
        if (exists(this.body)) {
            this.headers = {...this.headers, ...{'Content-Type': 'application/json'}};
        }
        return this.extractResponse(this.sendRequest('delete'));
    }

    private sendRequest<RESPONSETYPE>(method: AjaxRequestMethod): IHttpPromise<RESPONSETYPE> {
        const url = this.createUrl();
        const otherConfig = this.createConfig();
        return this.$http<RESPONSETYPE>({
            method,
            url,
            ...otherConfig
        });
    }

    private createUrl() {
        const serializedUrlParams = serializeUrlParams(this.params);
        if (serializedUrlParams.length === 0) {
            return this.baseUrl;
        }
        return this.baseUrl.includes('?') ? `${this.baseUrl}&${serializedUrlParams}` : `${this.baseUrl}?${serializedUrlParams}`;
    }

    private createConfig(): IRequestShortcutConfig {
        return {
            data: this.body,
            headers: this.headers,
            responseType: this.responseType,
        };
    }

    private async extractResponse<RESPONSETYPE>(request: IHttpPromise<RESPONSETYPE>): Promise<RESPONSETYPE> {
        try {
            const response = await request;
            return responseToData(response);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }
}

type AjaxRequestMethod = 'get' | 'post' | 'put' | 'delete';
type RequestBody = Record<string, any> | string;